// Last line of defence before anything leaves the device. Personal data must not reach Grafana
// Cloud, and we cannot audit every call site — micro-frontends log through the same patched
// console, and `serializeErrors` deep-serialises objects passed to console.error. Scrubbing
// centrally makes "no PII in Grafana" an invariant of the transport rather than a promise every
// call site has to keep.
//
// Order matters: the JWT pattern runs before the Bearer one so a bearer JWT is reported as
// `Bearer [redacted-token]` rather than losing the fact that it was a token at all.
const REDACTIONS: Array<[RegExp, string]> = [
	// Requires an alphabetic TLD so package specs such as `@grafana/faro-web-sdk@1.19.0`
	// are not mistaken for addresses and stripped out of otherwise useful messages.
	[/[\w.+-]+@[\w-]+(?:\.[\w-]+)*\.[a-z]{2,}/gi, '[redacted-email]'],
	// JWTs — access, id and refresh tokens all share this shape.
	[/\beyJ[\w-]*\.[\w-]*\.[\w-]*/g, '[redacted-token]'],
	[/\bBearer\s+[\w\-._~+/]+=*/gi, 'Bearer [redacted]'],
];

export function redact(value: string): string {
	return REDACTIONS.reduce((acc, [pattern, replacement]) => acc.replace(pattern, replacement), value);
}

export function redactRecord(record?: Record<string, string>): Record<string, string> | undefined {
	if (!record) {
		return record;
	}

	return Object.entries(record).reduce<Record<string, string>>((acc, [key, value]) => {
		acc[key] = typeof value === 'string' ? redact(value) : value;
		return acc;
	}, {});
}

/**
 * Returns the scrubbed free-text fields of a Faro signal payload, for the caller to merge back.
 * `message` (logs) and `value` (exceptions) can carry anything; context/attributes are ours but
 * may embed API responses. Measurement `values` are numeric and cannot hold personal data, so
 * they are left alone. Pure so it can be tested on its own.
 */
export default function scrubPersonalData(payload: Record<string, unknown>): Record<string, unknown> {
	const scrubbed: Record<string, unknown> = {};

	if (typeof payload.message === 'string') {
		scrubbed.message = redact(payload.message);
	}
	if (typeof payload.value === 'string') {
		scrubbed.value = redact(payload.value);
	}
	if (payload.context) {
		scrubbed.context = redactRecord(payload.context as Record<string, string>);
	}
	if (payload.attributes) {
		scrubbed.attributes = redactRecord(payload.attributes as Record<string, string>);
	}

	return scrubbed;
}
